import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Instagram, ArrowUpRight } from "lucide-react";

const Card = ({ item, catId, idx }) => {
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.5, delay: idx * 0.05, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={onMove}
      data-testid={`catalog-card-${item.code.toLowerCase()}`}
      className="card-spotlight group border border-[#28303f] bg-[#141923] transition-colors duration-300 hover:border-amber-500/50"
    >
      {item.img ? (
        <div className="img-clip relative aspect-[4/3] overflow-hidden">
          <img
            src={item.img}
            alt={item.title}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <span className="font-mono-tech absolute left-4 top-4 bg-[#07090e]/80 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-400 backdrop-blur-sm">
            {item.code}
          </span>
        </div>
      ) : (
        <div className="img-clip relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-gradient-to-br from-[#1a2130] to-[#0f131a]">
          <span className="font-display text-stroke pointer-events-none absolute select-none text-[9rem] font-black leading-none opacity-30">
            {item.code.slice(-2)}
          </span>
          <span className="h-3 w-3 rotate-45 bg-amber-400 transition-transform duration-500 group-hover:rotate-[135deg]" />
          <span className="font-mono-tech absolute left-4 top-4 bg-[#07090e]/80 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-400 backdrop-blur-sm">
            {item.code}
          </span>
        </div>
      )}
      <div className="p-6">
        <h3 className="font-display text-xl font-bold uppercase tracking-wide text-slate-100">{item.title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-slate-400">{item.specs}</p>
      </div>
    </motion.article>
  );
};

export const Catalog = ({ catalog }) => {
  const categories = catalog?.categories || [];
  const [active, setActive] = useState("gudang");
  const current = categories.find((c) => c.id === active) || categories[0];

  const waLink = current
    ? `https://wa.me/${catalog.company.wa_number}?text=${encodeURIComponent(current.wa_message)}`
    : "#";

  return (
    <section id="katalog" data-testid="catalog-section" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="mb-14 flex flex-col gap-4 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <p className="font-mono-tech mb-4 text-xs uppercase tracking-[0.35em] text-amber-400" data-testid="catalog-eyebrow">
            Katalog Layanan
          </p>
          <h2 className="font-display text-4xl font-black uppercase leading-none tracking-tight sm:text-5xl lg:text-6xl">
            Satu Orbit,<br />
            <span className="text-stroke">Enam Sektor.</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-slate-400">
          Pilih kategori di bawah — setiap sektor terhubung langsung ke tim kami via WhatsApp dengan pesan otomatis yang sesuai.
        </p>
      </motion.div>

      <div data-testid="catalog-tabs" className="mb-12 grid grid-cols-2 gap-px border border-[#28303f] bg-[#28303f] lg:grid-cols-3">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => {
              setActive(cat.id);
              const target = document.getElementById("catalog-panel");
              if (target) {
                if (window.__lenis) window.__lenis.scrollTo(target, { offset: -100, duration: 1.1 });
                else target.scrollIntoView({ behavior: "smooth", block: "start" });
              }
            }}
            data-testid={`tab-${cat.id}-button`}
            className={`group relative flex flex-col items-start gap-2 bg-[#0f131a] p-5 text-left transition-colors duration-300 sm:p-6 ${active === cat.id ? "bg-[#141923]" : "hover:bg-[#141923]/60"}`}
          >
            <span className={`font-mono-tech text-[10px] uppercase tracking-[0.3em] ${active === cat.id ? "text-amber-400" : "text-slate-500"}`}>
              {cat.index}
            </span>
            <span className={`font-display text-lg font-bold uppercase leading-tight tracking-wide sm:text-xl ${active === cat.id ? "text-slate-100" : "text-slate-400 group-hover:text-slate-200"}`}>
              {cat.name}
            </span>
            <span className={`absolute inset-x-0 bottom-0 h-0.5 origin-left bg-amber-400 transition-transform duration-500 ${active === cat.id ? "scale-x-100" : "scale-x-0"}`} />
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {current && (
          <motion.div key={current.id} id="catalog-panel" data-testid={`catalog-panel-${current.id}`} className="scroll-mt-24">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mb-10 max-w-2xl text-base text-slate-400"
              data-testid={`catalog-tagline-${current.id}`}
            >
              {current.tagline}
            </motion.p>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
              {current.items.map((item, idx) => (
                <Card key={item.code} item={item} catId={current.id} idx={idx} />
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="mt-12 flex flex-col gap-4 sm:flex-row"
            >
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`wa-cta-${current.id}-button`}
                className="group flex flex-1 items-center justify-center gap-3 bg-[#22c55e] px-8 py-5 font-mono-tech text-xs uppercase tracking-[0.25em] text-[#04120a] transition-all duration-300 hover:-translate-y-1 hover:bg-[#16a34a] hover:shadow-[0_16px_40px_-12px_rgba(34,197,94,0.5)]"
              >
                <MessageCircle className="h-4 w-4" />
                Tanya {current.name} via WhatsApp
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
              <a
                href={catalog.company.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ig-cta-${current.id}-button`}
                className="group flex flex-1 items-center justify-center gap-3 border border-[#e1306c]/60 px-8 py-5 font-mono-tech text-xs uppercase tracking-[0.25em] text-[#f472a5] transition-all duration-300 hover:-translate-y-1 hover:bg-[#e1306c] hover:text-white hover:shadow-[0_16px_40px_-12px_rgba(225,48,108,0.5)]"
              >
                <Instagram className="h-4 w-4" />
                Lihat Katalog Lengkap di Instagram
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
